import { useCallback, useEffect, useState } from 'react';

import { ApiError, PaynowSettings, fetchPaynow, savePaynow, uploadItemImage } from '../../../api/client';
import { EMPTY_PAYNOW_FORM, LogoDisplay, PaynowForm, SetPaynowField, formFromSettings, isSamePaynowForm } from './types';

/**
 * The PayNow settings form's state and operations.
 *
 * Owns the editable form, the last saved form (the baseline for `dirty`), the
 * branding image and the request feedback. The live preview is a separate hook
 * so this one stays about the account settings.
 */
export function usePaynowSettings(onUnauthorized: () => void) {
  const [form, setForm] = useState<PaynowForm>(EMPTY_PAYNOW_FORM);
  const [savedForm, setSavedForm] = useState<PaynowForm | null>(null);
  const [logo, setLogo] = useState<LogoDisplay>({ url: null, name: null });
  // The backend's flag for "this account has a destination configured". It is
  // not derived from the form: an empty unsaved form is not the same state.
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  /** Every operation starts by clearing stale feedback from both slots. */
  const beginFeedback = useCallback(() => {
    setError('');
    setNotice('');
  }, []);

  const handleError = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      // A failure replaces any success message; the two never show together.
      setNotice('');
      setError(err instanceof Error ? err.message : fallback);
    },
    [onUnauthorized]
  );

  const setField: SetPaynowField = useCallback((key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const applySettings = useCallback((settings: PaynowSettings) => {
    const next = formFromSettings(settings);
    // The form and its saved baseline move together, in one write.
    setForm(next);
    setSavedForm(next);
    // The settings API returns the image URL but not its original filename, so
    // the display name is unknown here and stays null.
    setLogo({ url: settings.logo_url, name: null });
    setConfigured(settings.configured);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPaynow()
      .then((settings) => {
        if (cancelled) return;
        applySettings(settings);
      })
      .catch((err: unknown) => {
        if (!cancelled) handleError(err, 'Could not load your payment settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [handleError, applySettings]);

  const chooseLogo = useCallback(
    (file: File) => {
      setUploading(true);
      beginFeedback();
      // Reuses the catalogue image store: content-addressed, immutable, and
      // served from /api/images/{public_id}.
      uploadItemImage(file)
        .then((image) => {
          setLogo({ url: image.url, name: image.original_filename || null });
          setForm((current) => ({
            ...current,
            logoId: image.id,
            // A picture is a clear signal of intent, so a plain code starts
            // showing it rather than waiting for a second, separate choice.
            style: current.style === 'plain' ? 'logo' : current.style,
          }));
        })
        .catch((err: unknown) => handleError(err, 'Could not upload the image.'))
        .finally(() => setUploading(false));
    },
    [beginFeedback, handleError]
  );

  const removeLogo = useCallback(() => {
    // The picture styles all need an image to draw, so dropping the image also
    // drops back to the plain code.
    setForm((current) => ({ ...current, logoId: null, style: 'plain' }));
    setLogo({ url: null, name: null });
  }, []);

  const savePaymentSettings = useCallback(() => {
    setSaving(true);
    beginFeedback();
    savePaynow({
      type: form.kind,
      value: form.value,
      name: form.name,
      color: form.color,
      logo_id: form.logoId,
      style: form.style,
    })
      .then((settings) => {
        applySettings(settings);
        setNotice('Saved. The customer display uses this code from the next order.');
      })
      .catch((err: unknown) => handleError(err, 'Could not save your payment settings.'))
      .finally(() => setSaving(false));
  }, [form, beginFeedback, applySettings, handleError]);

  const removePaynow = useCallback(() => {
    setSaving(true);
    beginFeedback();
    savePaynow({ type: null })
      .then(() => {
        // applySettings also sets `configured` from what it is given, so there
        // is no separate setConfigured(false) here.
        applySettings({
          type: null,
          value: null,
          name: null,
          configured: false,
          color: null,
          logo_id: null,
          logo_url: null,
          style: null,
        });
        setNotice('Removed. The customer display shows no payment code.');
        setConfirmRemove(false);
      })
      .catch((err: unknown) => handleError(err, 'Could not clear your payment settings.'))
      .finally(() => setSaving(false));
  }, [beginFeedback, applySettings, handleError]);

  const dismissError = useCallback(() => setError(''), []);

  const dirty = savedForm !== null && !isSamePaynowForm(form, savedForm);

  return {
    form,
    setField,
    logo,
    configured,
    loading,
    saving,
    uploading,
    error,
    notice,
    confirmRemove,
    setConfirmRemove,
    dirty,
    chooseLogo,
    removeLogo,
    savePaymentSettings,
    removePaynow,
    dismissError,
  };
}
